/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IActionToggleProps } from './action-toggle.props';

/**
 * ActionToggle - This component is toggle button which switches between states
 * and for accessibility reasons are 2 different buttons.
 */
export default class ActionToggle extends React.PureComponent<IActionToggleProps> {
    private readonly _divRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    private _focusElement: HTMLElement | null = null;

    public constructor(props: Readonly<IActionToggleProps>) {
        super(props);
        this._onClick = this._onClick.bind(this);
    }

    public getSnapshotBeforeUpdate(): null {
        this._focusElement = null;
        if (this._divRef.current?.contains(document.activeElement)) {
            this._focusElement = document.activeElement as HTMLElement;
        }
        return null;
    }

    public render(): JSX.Element | null {
        const { className, children, toggled, onChange, ...props } = this.props;
        const classes = `action-toggle${className ? ' ' : ''}${className || ''}`;
        return React.Children.count(children) !== 2 ? null : (
            <div className={classes} {...props} ref={this._divRef}>
                {React.Children.map(children, (child: React.ReactNode, index: number) => {
                    const hidden = (!index && !!toggled) || (!!index && !toggled);

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    return React.cloneElement(child as any, { 'aria-hidden': hidden, onClick: this._onClick });
                })}
            </div>
        );
    }

    private _onClick(event: React.KeyboardEvent): void {
        event.stopPropagation();
        if (this.props.onChange) {
            this.props.onChange({ id: this.props.id, actionToggle: this, toggled: !!this.props.toggled, event });
        }
        if (this._divRef.current && this._focusElement) {
            const focusTarget = this._divRef.current.children[this.props.toggled ? 1 : 0] as HTMLElement;
            if (focusTarget !== document.activeElement) {
                focusTarget.focus();
            }
        }
    }
}
